import {
  useGetFailedPerSheetJobsQuery,
  useGetFailedPerSheetRecordsQuery,
  useUploadPerSheetMutation,
  type DailyPayRow,
  type DailyPayUploadPreview,
  type FailedDailyPayRecord,
} from '@/app/features/employee/emp-payroll';
import { previewPaySheetFile } from './pay-sheet-preview';
import { downloadCycleTemplate } from './pay-sheet-template';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { AlertCircleIcon } from '@/icons';
import { toast } from '@/lib/toast';
import { Button, Spin, type TableProps } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';

const columns: TableProps<FailedDailyPayRecord>['columns'] = [
  { title: 'Row', dataIndex: 'row_number', width: 70 },
  { title: 'Employee ID', dataIndex: 'employee_id', render: (v) => v || 'n/a' },
  { title: 'Name', dataIndex: 'name', render: (v) => v || 'n/a' },
  {
    title: 'Date',
    dataIndex: 'date',
    render: (v: string) => (v ? dayjs(v).format('DD MMM, YYYY') : 'n/a'),
  },
  { title: 'Payable', dataIndex: 'payable', render: (v) => v || 'n/a' },
  {
    title: 'Failure Reason',
    dataIndex: 'failure_reason',
    render: (v: string) => <span className="text-red-400">{v || 'n/a'}</span>,
  },
];

/** Enough rows to judge the file, short enough to stay inside the drawer. */
const CHANGE_PREVIEW_LIMIT = 8;

type Props = {
  open: boolean;
  onClose: () => void;
  /** Every date of the cycle on screen, in order. */
  dates: string[];
  /** The cycle's label, e.g. "01 Oct - 31 Oct 2026". */
  label: string;
  /** What the sheet holds today, used to preview what the file would change. */
  rows: DailyPayRow[];
};

const Stat = ({ label, value }: { label: string; value: number }) => (
  <div>
    <div className="text-xs text-white/40">{label}</div>
    <div className="text-lg font-semibold text-white">{value}</div>
  </div>
);

/** The sheet is daily only, so the upload is too. */
const TEMPLATE_TYPE = 'DAILY' as const;

/**
 * Full-cycle upload: every daily employee against every date of the cycle on
 * screen. Blank cells are left alone, so a partly filled file is safe.
 */
const PaySheetUpload = ({ open, onClose, dates, label, rows }: Props) => {
  const {
    open: errorModal,
    handleOpen,
    handleClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  /** What the staged file would do, refreshed every time a file is picked. */
  const [preview, setPreview] = useState<DailyPayUploadPreview | null>(null);

  const [previewing, setPreviewing] = useState(false);

  const [uploadSheet, { isLoading: uploading }] = useUploadPerSheetMutation();


  const handleDownloadTemplate = () => {
    if (!dates.length) {
      toast('error', 'This cycle has no dates to fill in.');
      return;
    }
    downloadCycleTemplate(
      rows,
      dates,
      `daily-pay-${dates[0]}-to-${dates[dates.length - 1]}.xlsx`,
    );
  };

  /**
   * Reads the picked file in the browser so nothing is a surprise. The server
   * has no dry run, so this is a warning only — the upload is parsed again
   * server side and its result is what counts.
   */
  const handleFileStaged = async (file: File | null) => {
    setPreview(null);
    if (!file) return;
    setPreviewing(true);
    try {
      setPreview(await previewPaySheetFile(file, rows));
    } catch {
      // A file this cannot read fails loudly on upload instead.
    } finally {
      setPreviewing(false);
    }
  };

  const handleUpload = async (file: File) => {
    await uploadSheet({ file, templateType: TEMPLATE_TYPE }).unwrap();
    setPreview(null);
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={onClose}
        title="Upload Full Cycle Sheet"
        subtitle={`.xlsx · daily employees · every date of ${label} · Yes or No`}
        uploadLoading={uploading}
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        onFileStaged={handleFileStaged}
        preview={
          previewing ? (
            <div className="mt-4 flex items-center gap-3 p-4 rounded-xl bg-theme-slate-800 border border-theme-gray-700">
              <Spin size="small" />
              <p className="text-white/60 text-sm">Reading the file...</p>
            </div>
          ) : preview ? (
            <div className="mt-4 rounded-xl bg-theme-slate-800 border border-theme-gray-700 p-4 space-y-4">
              <p className="text-sm font-semibold text-white">
                What this file will do
              </p>

              <div className="flex flex-wrap gap-x-10 gap-y-3">
                <Stat label="Employees" value={preview.employees} />
                <Stat label="Dates" value={preview.days} />
                <Stat label="Cells to apply" value={preview.entries} />
              </div>

              {/* Blank cells were never counted, so nothing here is a no-op */}
              {preview.overwrites > 0 && (
                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <AlertCircleIcon
                      size={18}
                      className="text-amber-400 shrink-0 mt-0.5"
                    />
                    <div>
                      <p className="text-amber-400 text-sm font-semibold">
                        {preview.overwrites} answer
                        {preview.overwrites === 1 ? '' : 's'} will change
                      </p>
                      <p className="text-amber-400/60 text-xs mt-0.5">
                        These days already hold the opposite answer and will be
                        replaced by the file.
                      </p>
                    </div>
                  </div>

                  {/* The flipped days themselves, so nothing is a surprise */}
                  <div className="mt-3 space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
                    {preview.changes.slice(0, CHANGE_PREVIEW_LIMIT).map((change) => (
                      <div
                        key={`${change.employee_id}-${change.date}`}
                        className="flex items-center justify-between gap-3 text-xs"
                      >
                        <span className="text-white/70 truncate">
                          {change.name}
                          <span className="text-white/35">
                            {' '}
                            · {dayjs(change.date).format('DD MMM')}
                          </span>
                        </span>
                        <span className="shrink-0 whitespace-nowrap">
                          <span
                            className={
                              change.from ? 'text-emerald-400' : 'text-red-400'
                            }
                          >
                            {change.from ? 'Yes' : 'No'}
                          </span>
                          <span className="text-white/30"> → </span>
                          <span
                            className={
                              change.to ? 'text-emerald-400' : 'text-red-400'
                            }
                          >
                            {change.to ? 'Yes' : 'No'}
                          </span>
                        </span>
                      </div>
                    ))}

                    {preview.changes.length > CHANGE_PREVIEW_LIMIT && (
                      <p className="text-white/35 text-xs pt-1">
                        and {preview.changes.length - CHANGE_PREVIEW_LIMIT} more
                      </p>
                    )}
                  </div>
                </div>
              )}

              {preview.failed > 0 && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                  <AlertCircleIcon
                    size={18}
                    className="text-red-400 shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="text-red-400 text-sm font-semibold">
                      {preview.failed} row
                      {preview.failed === 1 ? '' : 's'} will be skipped
                    </p>
                    <ul className="text-red-400/60 text-xs mt-1 space-y-0.5">
                      {preview.reasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {!preview.entries && (
                <p className="text-white/45 text-xs">
                  Every cell is blank, so nothing would change.
                </p>
              )}

              {preview.entries > 0 && !preview.overwrites && (
                <p className="text-white/45 text-xs">
                  No stored answer changes — every filled cell is either new or
                  the same as what is saved.
                </p>
              )}
            </div>
          ) : null
        }
        errorViewer={
          <Button
            size="middle"
            className="btn-primary-bordered !h-10"
            onClick={() => handleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<FailedDailyPayRecord>
        open={errorModal.open && errorModal.type === 'ERROR_SHOW'}
        onClose={handleClose}
        title="Pay Sheet Upload Errors"
        useGetJobsQuery={useGetFailedPerSheetJobsQuery as any}
        useGetRecordsQuery={useGetFailedPerSheetRecordsQuery as any}
        columns={columns}
      />
    </>
  );
};

export default PaySheetUpload;
